import React, { useState, useEffect, useRef } from "react";
import { ChevronRight, ArrowRight } from "lucide-react";
import axios from "axios";
import logo from "../assets/haraxy latest hero.png";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import ScheduleBooking from "./scheduling/ScheduleBooking";

const HeroSection = () => {
  const [heroSlides, setHeroSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const [isHovering, setIsHovering] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const exploreBtnRef = useRef(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8788';

  useEffect(() => {
    const fetchHeroSlides = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/content/hero-slides`);
        setHeroSlides(response.data);
      } catch (error) {
        console.error('Error fetching hero slides:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchHeroSlides();
  }, [backendUrl]);

  useEffect(() => {
    if (heroSlides.length === 0) return;

    const timer = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        setIsAnimating(false);
      }, 300);
    }, 5000);

    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const scrollToServices = () => {
    const element = document.querySelector("#services");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handleMouseMove = (e) => {
    if (!exploreBtnRef.current) return;
    const rect = exploreBtnRef.current.getBoundingClientRect();
    setCoords({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  if (isLoading) {
    return (
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-black">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 border-4 border-gray-600 border-t-silver rounded-full animate-spin"></div>
          <p className="text-silver text-lg font-mono">Loading Excellence...</p>
        </div>
      </section>
    );
  }

  if (heroSlides.length === 0) return null;

  const currentHero = heroSlides[currentSlide];

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden transition-all duration-1000"
      style={{ backgroundColor: currentHero.bgColor }}
    >
      {/* 🔲 Background Grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent"></div>
        <div className="grid grid-cols-8 grid-rows-6 h-full w-full">
          {Array.from({ length: 48 }).map((_, i) => (
            <div
              key={i}
              className="border border-white/10 animate-pulse"
              style={{ animationDelay: `${i * 0.1}s` }}
            ></div>
          ))}
        </div>
      </div>

      {/* ✍️ Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-20 pb-8">
        <div
          className={`transition-all duration-500 ${isAnimating
            ? "opacity-0 transform translate-y-10"
            : "opacity-100 transform translate-y-0"
            }`}
        >
          {/* 🏷️ Heading */}
          <div className="mb-8 sm:mb-12">
            <div className="mb-6 sm:mb-8 flex flex-col items-center">
              <p className="text-xs sm:text-sm font-mono tracking-widest text-white/90 mb-3 sm:mb-4 uppercase px-4">
                Enterprise IT Solutions & Consulting
              </p>
              <div className="flex-shrink-0 cursor-pointer">
                <img
                  src={logo}
                  alt="HARA-XY Logo"
                  className="h-16 sm:h-20 md:h-22 w-auto"
                  onClick={() =>
                    window.scrollTo({ top: 0, behavior: "smooth" })
                  }
                />
              </div>
            </div>
          </div>

          {/* 💬 Slide Details */}
          <div className="max-w-4xl mx-auto mb-8 sm:mb-12 px-4">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-6 leading-tight"
              style={{ color: currentHero.textColor }}
            >
              {currentHero.title}
            </h2>
            <p
              className="text-base sm:text-lg md:text-xl font-light mb-3 sm:mb-4 tracking-wide"
              style={{ color: currentHero.accentColor }}
            >
              {currentHero.subtitle}
            </p>
            <p
              className="text-sm sm:text-lg md:text-xl lg:text-2xl leading-relaxed font-light max-w-3xl mx-auto"
              style={{ color: currentHero.textColor }}
            >
              {currentHero.description}
            </p>
          </div>

          {/* 🔘 CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center mb-12 sm:mb-16 px-4">
            {/* 🌞 Glowing Glass Button */}
            <button
              ref={exploreBtnRef}
              onClick={scrollToServices}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => setIsHovering(false)}
              onMouseMove={handleMouseMove}
              className="group relative px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg flex items-center gap-2 overflow-hidden transition-all duration-300 hover:scale-105 w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-black/50"
              style={{
                background: "rgba(255, 255, 255, 0.15)",
                backdropFilter: "blur(12px)",
                WebkitBackdropFilter: "blur(12px)",
                border: "1px solid rgba(255, 255, 255, 0.3)",
                boxShadow: `0 8px 24px ${currentHero.accentColor}66`,
                color: "#ffffff",
                position: "relative",
                userSelect: "none",
              }}
              aria-label="Explore our services"
            >
              {/* ✨ Cursor-follow glow */}
              {isHovering && (
                <>
                  <div
                    className="absolute pointer-events-none rounded-full"
                    style={{
                      top: coords.y - 90,
                      left: coords.x - 90,
                      width: 180,
                      height: 180,
                      background: `radial-gradient(circle, ${currentHero.accentColor}e6, ${currentHero.accentColor}99 60%, transparent 85%)`,
                      filter: "blur(26px) brightness(2)",
                      zIndex: 0,
                      transition: "top 0.06s ease, left 0.06s ease",
                    }}
                  />
                  <div
                    className="absolute pointer-events-none rounded-full"
                    style={{
                      top: coords.y - 60,
                      left: coords.x - 60,
                      width: 120,
                      height: 120,
                      background: `radial-gradient(circle, ${currentHero.accentColor}b3, ${currentHero.accentColor}66, transparent 70%)`,
                      filter: "blur(18px) brightness(1.5)",
                      zIndex: 1,
                      transition: "top 0.06s ease, left 0.06s ease",
                    }}
                  />
                </>
              )}

              <span className="relative z-10 flex items-center gap-2">
                Explore Our Services
                <ArrowRight
                  size={20}
                  className="group-hover:translate-x-1 transition-transform duration-300"
                />
              </span>
            </button>

            <Dialog open={isBookingOpen} onOpenChange={setIsBookingOpen}>
              <DialogTrigger asChild>
                <button
                  className="border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-base sm:text-lg hover:bg-white hover:text-black transition-all duration-300 hover:scale-105 w-full sm:w-auto focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-black/50"
                  aria-label="Schedule a consultation"
                >
                  Schedule Consultation
                </button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-[500px] bg-black border-gray-800 p-8 pt-12">
                <DialogHeader className="mb-4">
                  <DialogTitle className="text-2xl font-bold text-silver">Book a Discovery Session</DialogTitle>
                </DialogHeader>
                <ScheduleBooking />
              </DialogContent>
            </Dialog>
          </div>

          {/* 🔘 Dot Indicators */}
          <div className="flex justify-center space-x-3 mb-8 sm:mb-0">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 sm:w-3 h-2 sm:h-3 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${index === currentSlide
                  ? "bg-white scale-125"
                  : "bg-white/40 hover:bg-white/70"
                  }`}
                aria-label={`Go to slide ${index + 1}`}
                aria-current={index === currentSlide ? "true" : "false"}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <button
          onClick={scrollToServices}
          className="text-white/70 hover:text-white transition-colors duration-300 sm:hidden focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-1"
          aria-label="Scroll to services"
        >
          <ChevronRight size={20} className="rotate-90" />
        </button>
        <button
          onClick={scrollToServices}
          className="text-white/70 hover:text-white transition-colors duration-300 hidden sm:block focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-1"
          aria-label="Scroll to services"
        >
          <ChevronRight size={24} className="rotate-90" />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
